import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BACKEND_URL } from '../../config';
import { useOrganization } from '../../context/OrganizationContext';
import { useCommand } from '../../context/CommandContext';
import { fetchWithOrg } from '../../utils/api';
import RunAgentModal from '../../components/RunAgentModal';
import SEO from '../../components/SEO';

import {
  ArrowRight,
  CheckCircle,
  Clock,
  Lightning,
  ListBullets,
  Play,
  Robot,
  Sparkle,
  Warning,
  WarningCircle
} from '@phosphor-icons/react';
/**
 * Employee Workspace Landing Page
 * Single-Login RBAC: Member/Viewer home after login
 */
const WorkspaceHome = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  // SINGLE SOURCE OF TRUTH: Use agentCount from CommandContext directly
  // DO NOT fetch agents separately - context handles this
  // Note: agentToRun handling moved to global GlobalRunAgentModal in App.js
  const { openCommand, agentCount: commandAgentCount } = useCommand();
  const [agents, setAgents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seatActive, setSeatActive] = useState(true);
  const [seatRequired, setSeatRequired] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hasSeenWelcome, setHasSeenWelcome] = useState(true);

  useEffect(() => {
    if (activeOrganization) {
      fetchWorkspaceData();
    }
    // Note: Command agents are fetched by CommandContext when Command opens
    // No need to refresh here - context handles lazy loading on first open
  }, [activeOrganization]);

  useEffect(() => {
    // Check if user has seen workspace welcome
    const checkWelcomeStatus = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSeatActive(data.seat_active || false);
          setHasSeenWelcome(data.has_seen_workspace_welcome || false);
        }
      } catch (error) {
        console.error('Failed to check welcome status:', error);
      }
    };
    checkWelcomeStatus();
  }, []);

  const fetchWorkspaceData = async () => {
    try {
      setLoading(true);

      // Fetch assigned agents
      const agentsResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/my/agents`,
        {},
        activeOrganization.organization_id
      );

      if (agentsResponse.ok) {
        const agentsData = await agentsResponse.json();
        setAgents(agentsData.agents || []);
        setSeatRequired(agentsData.seat_required || false);
      }

      // Fetch recent sessions
      const sessionsResponse = await fetchWithOrg(
        `${BACKEND_URL}/api/my/sessions?limit=5`,
        {},
        activeOrganization.organization_id
      );

      if (sessionsResponse.ok) {
        const sessionsData = await sessionsResponse.json();
        setSessions(sessionsData.sessions || []);
      }
    } catch (err) {
      console.error('Error fetching workspace data:', err);
    } finally {
      setLoading(false);
    }
  };

  const dismissWelcome = async () => {
    setHasSeenWelcome(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ has_seen_workspace_welcome: true })
      });
      
      if (response.ok) {
        console.log('Welcome dismissal saved successfully');
      } else {
        console.error('Failed to save welcome dismissal:', await response.text());
      }
    } catch (error) {
      console.error('Failed to update welcome status:', error);
    }
  };

  const handleRunAgent = (agent) => {
    if (!seatActive || seatRequired) {
      return; // Prevent running if no seat
    }
    setSelectedAgent(agent);
    setModalOpen(true);
  };

  const handleSessionSuccess = (sessionId) => {
    // Don't close modal or navigate immediately - let the success modal show first
    // User can click "View Results" to navigate to the session
    console.log('Session completed:', sessionId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'running': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return CheckCircle;
      case 'failed': return WarningCircle;
      case 'running': return Clock;
      default: return Clock;
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Quick action cards - Command is PRIMARY
  const quickActions = [
    {
      title: 'Open Command',
      description: 'Run your assigned AI agents',
      icon: Lightning,
      iconBg: 'bg-[#6366F1]/15',
      iconColor: 'text-[#6366F1]',
      action: () => openCommand(),
      disabled: !seatActive || seatRequired,
      isPrimary: true,
      badge: commandAgentCount > 0 ? commandAgentCount : null
    },
    {
      title: 'My Agents',
      description: 'View all assigned agents',
      icon: Robot,
      iconBg: 'bg-blue-500/15',
      iconColor: 'text-blue-400',
      action: () => navigate('/partner-pro/workspace/my-agents'),
      disabled: false
    },
    {
      title: 'My Sessions',
      description: 'View execution history',
      icon: ListBullets,
      iconBg: 'bg-emerald-500/15',
      iconColor: 'text-emerald-400',
      action: () => navigate('/partner-pro/workspace/sessions'),
      disabled: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#161B26] text-white">
      <SEO 
        title="Workspace - Man & Machine"
        description="Your AI-powered workspace"
      />

      {/* First-Time Welcome Banner */}
      {!hasSeenWelcome && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-xl"
        >
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[#6366F1]/20 flex items-center justify-center flex-shrink-0">
                <Sparkle size={20} className="text-[#6366F1]" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold mb-1">Welcome to Your Workspace</h3>
                <p className="text-[#A0AEC0] text-sm mb-3">
                  Your administrator has granted you access to specific agents and workflows. 
                  <strong className="text-white"> Privacy-first by design:</strong> You only see the tools assigned to you.
                </p>
                <button
                  onClick={dismissWelcome}
                  className="text-sm text-[#6366F1] hover:text-[#818CF8] font-medium transition-colors"
                >
                  Got it, don&apos;t show again
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Seat Required Banner */}
      {(!seatActive || seatRequired) && (
        <div className="bg-[#EF4444]/10 border-b border-[#EF4444]/30">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-start gap-4">
              <Warning className="text-[#EF4444] flex-shrink-0" size={24} />
              <div>
                <h3 className="text-white font-semibold mb-1">Active Seat Required</h3>
                <p className="text-[#A0AEC0] text-sm">
                  Your administrator needs to assign you an active seat to run agents. Please contact your admin to request access.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Workspace</h1>
          <div className="flex items-center gap-2 text-[#A0AEC0] text-sm">
            <Lightning size={16} className="text-[#EC4899]" />
            <span>Press <kbd className="px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.1)] text-white font-mono text-xs">⌘K</kbd> to open Command</span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action) => {
              const Icon = action.icon;
              const isPrimary = action.isPrimary;
              return (
                <button
                  key={action.title}
                  onClick={action.action}
                  disabled={action.disabled}
                  className={`group text-left rounded-xl p-6 transition-all duration-200 ${
                    isPrimary 
                      ? 'bg-[#6366F1]/10 border-2 border-[#6366F1]/30 hover:border-[#6366F1]'
                      : 'bg-[#1A1B23] border border-[#2D2E3A] hover:border-[#6366F1]'
                  } ${
                    action.disabled 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'cursor-pointer'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg ${action.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={24} className={action.iconColor} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold mb-1 ${
                          isPrimary ? 'text-white' : 'text-white'
                        } ${
                          !action.disabled && (isPrimary ? 'group-hover:text-[#EC4899]' : 'group-hover:text-[#6366F1]')
                        } transition-colors`}>
                          {action.title}
                        </h3>
                        {action.badge && (
                          <span className="px-2 py-0.5 rounded-full bg-[#6366F1] text-white text-xs font-medium">
                            {action.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#A0AEC0]">{action.description}</p>
                      {isPrimary && (
                        <p className="text-xs text-[#6366F1] mt-2 font-medium">
                          ⌘K
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* My Agents Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">My Agents</h2>
            <Link 
              to="/partner-pro/workspace/my-agents"
              className="text-sm text-[#6366F1] hover:text-[#818CF8] flex items-center gap-1 transition-colors"
            >
              View all
              <ArrowRight size={16} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#1A1B23] border border-[#2D2E3A] rounded-xl p-6 animate-pulse">
                  <div className="w-12 h-12 bg-[#2D2E3A] rounded-lg mb-4"></div>
                  <div className="h-4 bg-[#2D2E3A] rounded mb-2"></div>
                  <div className="h-3 bg-[#2D2E3A] rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : agents.length === 0 ? (
            <div className="bg-[#1A1B23] border border-[#2D2E3A] rounded-xl p-12 text-center">
              <Robot size={48} className="text-[#6366F1] mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">No Agents Assigned Yet</h3>
              <p className="text-[#A0AEC0] text-sm mb-4">
                Your administrator hasn&apos;t assigned you any agents yet.
              </p>
              <p className="text-[#A0AEC0] text-sm">
                Contact your admin to request access to specific agents.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {agents.slice(0, 6).map((agent) => (
                <div
                  key={agent.id || agent.agent_id}
                  className="bg-[#1A1B23] border border-[#2D2E3A] rounded-xl p-6 hover:border-[#6366F1] transition-all duration-200"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-lg bg-[#6366F1]/15 flex items-center justify-center">
                      <Robot size={24} className="text-[#6366F1]" />
                    </div>
                    {agent.readiness_state === 'ready' && (
                      <CheckCircle size={20} className="text-green-400" />
                    )}
                  </div>
                  <h3 className="text-white font-semibold mb-2">{agent.name}</h3>
                  <p className="text-[#A0AEC0] text-sm mb-4 line-clamp-2">
                    {agent.description || 'No description available'}
                  </p>
                  <button
                    onClick={() => handleRunAgent(agent)}
                    disabled={!seatActive || seatRequired}
                    className={`w-full py-2 rounded-lg font-medium transition-all ${
                      !seatActive || seatRequired
                        ? 'bg-[#2D2E3A] text-[#A0AEC0] cursor-not-allowed'
                        : 'bg-[#6366F1] hover:bg-[#5558E6] text-white'
                    }`}
                  >
                    <Play size={16} className="inline mr-2" />
                    Run Agent
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Sessions */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Recent Sessions</h2>
            <Link 
              to="/partner-pro/workspace/sessions"
              className="text-sm text-[#6366F1] hover:text-[#818CF8] flex items-center gap-1 transition-colors"
            >
              View all
              <ArrowRight size={16} />
            </Link>
          </div>

          {loading ? (
            <div className="bg-[#1A1B23] border border-[#2D2E3A] rounded-xl p-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 py-4 animate-pulse">
                  <div className="w-10 h-10 bg-[#2D2E3A] rounded-lg"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-[#2D2E3A] rounded mb-2"></div>
                    <div className="h-3 bg-[#2D2E3A] rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="bg-[#1A1B23] border border-[#2D2E3A] rounded-xl p-12 text-center">
              <Clock size={48} className="text-[#6366F1] mx-auto mb-4" />
              <h3 className="text-white font-semibold mb-2">No Sessions Yet</h3>
              <p className="text-[#A0AEC0] text-sm">
                Run your first agent to see your execution history here.
              </p>
            </div>
          ) : (
            <div className="bg-[#1A1B23] border border-[#2D2E3A] rounded-xl overflow-hidden">
              {sessions.map((session, index) => {
                const StatusIcon = getStatusIcon(session.status);
                return (
                  <Link
                    key={session.session_id}
                    to={`/partner-pro/workspace/sessions/${session.session_id}`}
                    className={`flex items-center gap-4 p-4 hover:bg-[#2D2E3A] transition-colors ${
                      index < sessions.length - 1 ? 'border-b border-[#2D2E3A]' : ''
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg bg-[#6366F1]/10 flex items-center justify-center`}>
                      <StatusIcon size={20} className={getStatusColor(session.status)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">{session.agent_name || 'Unknown Agent'}</h4>
                      <p className="text-[#A0AEC0] text-sm">
                        {session.status} • {formatTimeAgo(session.created_at)}
                      </p>
                    </div>
                    <ArrowRight size={20} className="text-[#A0AEC0] flex-shrink-0" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Run Agent Modal */}
      {selectedAgent && (
        <RunAgentModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          agent={selectedAgent}
          onSuccess={handleSessionSuccess}
        />
      )}
    </div>
  );
};

export default WorkspaceHome;
